import { useState } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { storeConsent } from "@/lib/consent-storage";

export function SignupAcceptanceGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (confirmed) return <>{children}</>;

  return (
    <div
      className="w-full max-w-[420px] mx-auto bg-[#0a0a12] border border-white/[0.06] rounded-2xl p-6 shadow-[0_0_80px_hsl(180_60%_40%/0.06)]"
      data-testid="signup-acceptance-gate"
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
          <ShieldCheck className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-white">{t("legal.gateTitle")}</h2>
          <p className="text-[11px] text-white/45">{t("legal.gateSubtitle")}</p>
        </div>
      </div>

      <p className="text-xs text-white/55 leading-relaxed mb-5">{t("legal.gateIntro")}</p>

      <label className="flex items-start gap-2.5 cursor-pointer mb-5 group">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-white/30 bg-white/[0.04] text-primary focus:ring-1 focus:ring-primary/50 cursor-pointer"
          data-testid="signup-acceptance-checkbox"
        />
        <span className="text-xs text-white/70 leading-relaxed">
          {t("legal.acceptanceCheckboxPrefix")}{" "}
          <Link
            href="/legal/terms"
            target="_blank"
            className="text-primary underline-offset-2 hover:underline"
          >
            {t("legal.termsOfService")}
          </Link>
          {", "}
          <Link
            href="/legal/privacy"
            target="_blank"
            className="text-primary underline-offset-2 hover:underline"
          >
            {t("legal.privacyPolicy")}
          </Link>
          {", "}
          {t("legal.acceptanceCheckboxAnd")}{" "}
          <Link
            href="/legal/disclaimer"
            target="_blank"
            className="text-primary underline-offset-2 hover:underline"
          >
            {t("legal.healthDisclaimer")}
          </Link>
          .
        </span>
      </label>

      <Button
        className="w-full"
        size="lg"
        disabled={!accepted}
        data-testid="signup-acceptance-continue"
        onClick={() => {
          storeConsent();
          setConfirmed(true);
        }}
      >
        {t("legal.gateContinue")}
      </Button>

      <p className="text-[10px] text-white/35 text-center mt-3 leading-relaxed">
        {t("legal.gateAuditNotice")}
      </p>
    </div>
  );
}
